import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { ArrowLeft, Download, Eye, Plus, CreditCard, Mail, Send, X, Trash2, AlertTriangle } from "lucide-react";
import {
  getInvoice,
  listPayments,
  recordPayment,
  sendInvoice,
  deleteInvoice,
  getBillingSettings,
  viewInvoicePdf,
  downloadInvoicePdf,
  computeTotals,
} from "../../services/invoiceService";
import { useAuthedImage } from "../../hooks/useAuthedImage";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const field = { fontSize: 12.5, border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 10px", outline: "none", width: "100%" };
const label = { fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 5 };

const STATUS_META = {
  draft:   { bg: "#f1f5f9", color: "#475569", label: "Draft" },
  sent:    { bg: "#eff6ff", color: "#1d4ed8", label: "Sent" },
  partial: { bg: "#fff7ed", color: "#c2410c", label: "Partial" },
  paid:    { bg: "#f0fdf4", color: "#166534", label: "Paid" },
};

const Card = ({ title, children, extra }) => (
  <div style={{ background: "#fff", border: "1px solid var(--borderColor)", borderRadius: 14, boxShadow: "0 1px 3px rgba(15,23,42,.05)", overflow: "hidden", marginBottom: 16 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", borderBottom: "1px solid var(--borderColor)", background: "#fafbfc" }}>
      <h6 style={{ margin: 0, fontWeight: 700, fontSize: 13, color: "#0f172a" }}>{title}</h6>
      {extra}
    </div>
    <div style={{ padding: 18 }}>{children}</div>
  </div>
);

const EMPTY_PAYMENT = { amount: "", paidAt: "", method: "", note: "" };

const RecordPaymentModal = ({ show, onClose, onSubmit, submitting, maxDue, currencySymbol }) => {
  const [form, setForm] = useState(EMPTY_PAYMENT);

  useEffect(() => {
    if (show) setForm(EMPTY_PAYMENT);
  }, [show]);

  const handleSubmit = () => {
    const amount = Number(form.amount);
    if (!amount || amount <= 0) return;
    onSubmit({
      amount,
      paidAt: form.paidAt ? new Date(form.paidAt).toISOString() : undefined,
      method: form.method || undefined,
      note: form.note || undefined,
    });
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title style={{ fontSize: 16, fontWeight: 800 }}>Record a Payment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-2">
          <div className="col-12">
            <label style={label}>Amount ({currencySymbol})</label>
            <input type="number" min="0.01" step="0.01" style={field} value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
            {maxDue > 0 && <p style={{ margin: "4px 0 0", fontSize: 11, color: "#94a3b8" }}>Amount due: {currencySymbol}{maxDue.toFixed(2)}</p>}
          </div>
          <div className="col-12">
            <label style={label}>Paid On</label>
            <input type="datetime-local" style={field} value={form.paidAt} onChange={(e) => setForm((f) => ({ ...f, paidAt: e.target.value }))} />
            <p style={{ margin: "4px 0 0", fontSize: 11, color: "#94a3b8" }}>Leave blank to use the current date/time.</p>
          </div>
          <div className="col-12">
            <label style={label}>Method</label>
            <input style={field} placeholder="e.g. bank_transfer, card, cash" value={form.method} onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))} />
          </div>
          <div className="col-12">
            <label style={label}>Note</label>
            <textarea rows={2} style={field} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-0">
        <button className="btn btn-outline-secondary btn-sm" style={{ borderRadius: 8 }} onClick={onClose} disabled={submitting}>Cancel</button>
        <button className="btn btn-primary btn-sm d-flex align-items-center gap-2" style={{ borderRadius: 8 }} onClick={handleSubmit} disabled={submitting || !form.amount}>
          <CreditCard size={14} /> {submitting ? "Recording…" : "Record Payment"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

const SendEmailModal = ({ show, onClose, onSubmit, submitting, defaultEmails }) => {
  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");

  useEffect(() => {
    if (show) {
      setEmails(defaultEmails);
      setEmailInput("");
    }
  }, [show, defaultEmails]);

  const addEmail = () => {
    const email = emailInput.trim();
    if (!email || !EMAIL_RE.test(email)) return;
    if (!emails.includes(email)) setEmails((list) => [...list, email]);
    setEmailInput("");
  };
  const removeEmail = (email) => setEmails((list) => list.filter((e) => e !== email));

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title style={{ fontSize: 16, fontWeight: 800 }}>Send Invoice by Email</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <label style={label}>Recipients</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: emails.length ? 10 : 0 }}>
          {emails.map((email) => (
            <span key={email} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "#334155", background: "#f1f5f9", borderRadius: 999, padding: "5px 6px 5px 12px" }}>
              {email}
              <button type="button" onClick={() => removeEmail(email)} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", display: "flex", alignItems: "center", padding: 2, borderRadius: "50%" }}>
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="email"
            style={field}
            placeholder="Add another email"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
          />
          <button type="button" className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1" style={{ borderRadius: 7, flexShrink: 0 }} onClick={addEmail}>
            <Plus size={13} /> Add
          </button>
        </div>
        <p style={{ margin: "8px 0 0", fontSize: 11, color: "#94a3b8" }}>
          Pre-filled from Billing Settings. Changes here are only used for this invoice and won't update your saved billing emails.
        </p>
      </Modal.Body>
      <Modal.Footer className="border-0">
        <button className="btn btn-outline-secondary btn-sm" style={{ borderRadius: 8 }} onClick={onClose} disabled={submitting}>Cancel</button>
        <button className="btn btn-primary btn-sm d-flex align-items-center gap-2" style={{ borderRadius: 8 }} onClick={() => onSubmit(emails)} disabled={submitting || emails.length === 0}>
          <Send size={14} /> {submitting ? "Sending…" : "Send"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

const InvoiceDetails = () => {
  const { id: paramProjectPid, invoiceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const projectPid = paramProjectPid || location.state?.projectPid;
  const backPath = paramProjectPid ? `/project-details/${projectPid}/invoices` : "/admin-invoices";

  const [invoice, setInvoice] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [recording, setRecording] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [billingEmails, setBillingEmails] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const logoSrc = useAuthedImage(invoice?.theme?.logoUrl);

  useEffect(() => {
    if (!projectPid) return;
    getBillingSettings(projectPid)
      .then((res) => setBillingEmails(res.data?.response?.billingEmails ?? []))
      .catch(() => {});
  }, [projectPid]);

  const load = () => {
    setLoading(true);
    Promise.all([
      getInvoice(invoiceId),
      listPayments(invoiceId).catch(() => ({ data: { response: [] } })),
    ])
      .then(([invRes, payRes]) => {
        setInvoice(invRes.data.response);
        setPayments(payRes.data?.response ?? []);
      })
      .catch(() => navigate(backPath))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!invoiceId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  const handleViewPdf = async () => {
    if (!invoice?.pdfUrl) return;
    setOpening(true);
    try {
      await viewInvoicePdf(invoice.pdfUrl);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to open PDF");
    } finally {
      setOpening(false);
    }
  };

  const handleDownloadPdf = async () => {
    setExporting(true);
    try {
      await downloadInvoicePdf(invoice.invoicePid, `${invoice.invoiceNumber || "invoice"}.pdf`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to download PDF");
    } finally {
      setExporting(false);
    }
  };

  const handleRecordPayment = async (payload) => {
    setRecording(true);
    try {
      const res = await recordPayment(invoice.invoicePid, payload);
      setInvoice(res.data.response);
      setShowPaymentModal(false);
      toast.success("Payment recorded successfully");
      listPayments(invoice.invoicePid).then((r) => setPayments(r.data?.response ?? [])).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to record payment");
    } finally {
      setRecording(false);
    }
  };

  const handleSendInvoice = async (emails) => {
    setSending(true);
    try {
      const res = await sendInvoice(invoice.invoicePid, emails.join(","));
      setInvoice((inv) => ({ ...inv, ...res.data.response }));
      setShowSendModal(false);
      toast.success("Invoice sent successfully");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to send invoice");
    } finally {
      setSending(false);
    }
  };

  const handleDeleteInvoice = async () => {
    setDeleting(true);
    try {
      await deleteInvoice(invoice.invoicePid);
      toast.success("Invoice deleted successfully");
      navigate(backPath);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete invoice");
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading || !invoice) {
    return (
      <main className="app-wrapper url-list-page">
        <div className="container-fluid py-5 text-center text-muted">Loading…</div>
      </main>
    );
  }

  const totals = computeTotals(invoice);
  const accent = invoice.theme?.accentColor || "#2563eb";
  const st = STATUS_META[invoice.status] || STATUS_META.draft;
  const paidAmount = Number(invoice.paidAmount ?? 0);
  const dueAmount = Number(invoice.dueAmount ?? Math.max(0, totals.total - paidAmount));
  const canRecordPayment = invoice.status === "sent" || invoice.status === "partial";

  return (
    <main className="app-wrapper url-list-page">
      <div className="container-fluid">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" style={{ borderRadius: 8 }}
              onClick={() => navigate(backPath)}>
              <ArrowLeft size={14} /> Invoices
            </button>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <h5 style={{ margin: 0, fontWeight: 800, color: "#0f172a" }}>{invoice.invoiceNumber}</h5>
                <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: st.bg, color: st.color }}>{st.label}</span>
              </div>
              <p style={{ margin: 0, fontSize: 12, color: "var(--muteColor)" }}>{invoice.to?.name || "Client not set"}</p>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {invoice.status === "draft" && (
              <button className="btn btn-outline-success btn-sm d-flex align-items-center gap-2" style={{ borderRadius: 8 }} onClick={() => setShowSendModal(true)}>
                <Mail size={14} /> Send Email
              </button>
            )}
            <button className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2" style={{ borderRadius: 8 }} onClick={handleViewPdf} disabled={!invoice.pdfUrl || opening}>
              <Eye size={14} /> {opening ? "Opening…" : "View PDF"}
            </button>
            <button className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2" style={{ borderRadius: 8 }} onClick={handleDownloadPdf} disabled={exporting}>
              <Download size={14} /> {exporting ? "Exporting…" : "Download PDF"}
            </button>
            {invoice.status === "draft" && (
              <button className="btn btn-outline-danger btn-sm d-flex align-items-center gap-2" style={{ borderRadius: 8 }} onClick={() => setShowDeleteModal(true)}>
                <Trash2 size={14} /> Delete
              </button>
            )}
          </div>
        </div>

        <div className="row g-3">
          <div className="col-12 col-xl-7">
            <Card title="Invoice">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
                <div>
                  {logoSrc && <img src={logoSrc} alt="logo" style={{ height: 40, marginBottom: 8 }} />}
                  <p style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{invoice.from?.name || "Your Business"}</p>
                  <p style={{ margin: 0, fontSize: 12, whiteSpace: "pre-line", color: "#475569" }}>{invoice.from?.details}</p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <h3 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: accent, letterSpacing: "0.02em" }}>INVOICE</h3>
                  <p style={{ margin: "4px 0 0", fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{invoice.invoiceNumber}</p>
                  <p style={{ margin: "6px 0 0", fontSize: 11, color: "#64748b" }}>Issued: {invoice.issueDate || "—"}</p>
                  <p style={{ margin: "2px 0 0", fontSize: 11, color: "#64748b" }}>Due: {invoice.dueDate || "—"}</p>
                  {invoice.poNumber && <p style={{ margin: "2px 0 0", fontSize: 11, color: "#64748b" }}>PO: {invoice.poNumber}</p>}
                  <p style={{ margin: "6px 0 0", fontSize: 14, fontWeight: 800, color: accent }}>
                    Total: {invoice.currencySymbol}{(invoice.total ?? totals.total).toFixed(2)}
                  </p>
                </div>
              </div>

              <p style={{ margin: "0 0 4px", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase" }}>Bill To</p>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>{invoice.to?.name || "Client Name"}</p>
              <p style={{ margin: "0 0 16px", fontSize: 12, whiteSpace: "pre-line", color: "#475569" }}>{invoice.to?.details}</p>

              <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", marginBottom: 16 }}>
                <thead>
                  <tr style={{ background: accent }}>
                    <th style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, color: "#fff", fontWeight: 700 }}>Description</th>
                    <th style={{ textAlign: "right", padding: "8px 10px", fontSize: 11, color: "#fff", fontWeight: 700, width: 60 }}>Qty</th>
                    <th style={{ textAlign: "right", padding: "8px 10px", fontSize: 11, color: "#fff", fontWeight: 700, width: 90 }}>Rate</th>
                    <th style={{ textAlign: "right", padding: "8px 10px", fontSize: 11, color: "#fff", fontWeight: 700, width: 90 }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(invoice.items || []).map((it, i) => {
                    const isUsage = it.itemType === "usage";
                    return (
                      <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "8px 10px", fontSize: 12 }}>{it.description || "—"}</td>
                        <td style={{ padding: "8px 10px", fontSize: 12, textAlign: "right" }}>{isUsage ? "-" : (it.quantity || 0)}</td>
                        <td style={{ padding: "8px 10px", fontSize: 12, textAlign: "right" }}>{isUsage ? "-" : `${invoice.currencySymbol}${(Number(it.rate) || 0).toFixed(2)}`}</td>
                        <td style={{ padding: "8px 10px", fontSize: 12, textAlign: "right", fontWeight: 600 }}>{invoice.currencySymbol}{(it.amount ?? (Number(it.quantity) || 0) * (Number(it.rate) || 0)).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <div style={{ width: 260 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0", color: "#475569" }}>
                    <span>Subtotal</span><span>{invoice.currencySymbol}{(invoice.subtotal ?? totals.subtotal).toFixed(2)}</span>
                  </div>
                  {(invoice.taxes || []).map((t, i) => (
                    <div key={`tax-${i}`} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0", color: "#475569" }}>
                      <span>{t.label || "Tax"} {t.type === "percent" ? `(${t.value || 0}%)` : ""}</span><span>{invoice.currencySymbol}{Number(t.amount || 0).toFixed(2)}</span>
                    </div>
                  ))}
                  {(invoice.discounts || []).map((d, i) => (
                    <div key={`disc-${i}`} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0", color: "#475569" }}>
                      <span>{d.label || "Discount"} {d.type === "percent" ? `(${d.value || 0}%)` : ""}</span><span>-{invoice.currencySymbol}{Number(d.amount || 0).toFixed(2)}</span>
                    </div>
                  ))}
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: 800, padding: "8px 0 0", marginTop: 6, borderTop: `2px solid ${accent}`, color: accent }}>
                    <span>Total</span><span>{invoice.currencySymbol}{(invoice.total ?? totals.total).toFixed(2)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "8px 0 0", color: "#166534" }}>
                    <span>Paid</span><span>{invoice.currencySymbol}{paidAmount.toFixed(2)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700, padding: "2px 0 0", color: dueAmount > 0 ? "#c2410c" : "#166534" }}>
                    <span>Due</span><span>{invoice.currencySymbol}{dueAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {(invoice.customFields || []).filter((f) => f.value).map((f, i) => (
                <div key={i} style={{ marginTop: 16 }}>
                  <p style={{ margin: "0 0 3px", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase" }}>{f.label || "Note"}</p>
                  <p style={{ margin: 0, fontSize: 12, color: "#475569", whiteSpace: "pre-line" }}>{f.value}</p>
                </div>
              ))}
            </Card>
          </div>

          <div className="col-12 col-xl-5">
            <Card title="Payment History" extra={
              canRecordPayment && (
                <button type="button" className="btn btn-primary btn-sm d-flex align-items-center gap-1" style={{ borderRadius: 7 }} onClick={() => setShowPaymentModal(true)}>
                  <Plus size={13} /> Record Payment
                </button>
              )
            }>
              {payments.length === 0 ? (
                <p style={{ margin: 0, fontSize: 13, color: "#94a3b8", textAlign: "center", padding: "16px 0" }}>No payments recorded yet.</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {payments.map((p, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "10px 0", borderBottom: i === payments.length - 1 ? "none" : "1px solid #f1f5f9" }}>
                      <div>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{invoice.currencySymbol}{Number(p.amount || 0).toFixed(2)}</p>
                        <p style={{ margin: "2px 0 0", fontSize: 11, color: "#94a3b8" }}>{p.paidAt ? new Date(p.paidAt).toLocaleString() : "—"}</p>
                        {p.note && <p style={{ margin: "2px 0 0", fontSize: 11, color: "#64748b" }}>{p.note}</p>}
                      </div>
                      {p.method && <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b", background: "#f1f5f9", borderRadius: 999, padding: "3px 9px", flexShrink: 0 }}>{p.method}</span>}
                    </div>
                  ))}
                </div>
              )}
              {!canRecordPayment && invoice.status === "draft" && (
                <p style={{ margin: "10px 0 0", fontSize: 11, color: "#94a3b8" }}>Send this invoice before recording a payment.</p>
              )}
            </Card>
          </div>
        </div>
      </div>

      <RecordPaymentModal
        show={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSubmit={handleRecordPayment}
        submitting={recording}
        maxDue={dueAmount}
        currencySymbol={invoice.currencySymbol}
      />

      <SendEmailModal
        show={showSendModal}
        onClose={() => setShowSendModal(false)}
        onSubmit={handleSendInvoice}
        submitting={sending}
        defaultEmails={billingEmails}
      />

      {showDeleteModal && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => !deleting && setShowDeleteModal(false)}
        >
          <div
            style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 420, boxShadow: "0 8px 32px rgba(15,23,42,0.18)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid var(--borderColor)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: "#fff1f2", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <AlertTriangle size={17} />
                </div>
                <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Delete Invoice</h6>
              </div>
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: 4 }} onClick={() => setShowDeleteModal(false)} disabled={deleting}>
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: "20px" }}>
              <p style={{ margin: "0 0 6px", fontSize: 14, color: "#334155" }}>
                Are you sure you want to delete <strong>{invoice.invoiceNumber}</strong>?
              </p>
              <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>
                This cannot be undone. Any usage billed to this invoice will be unbilled and eligible for the next invoice.
              </p>
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", padding: "0 20px 20px" }}>
              <button className="btn btn-light" style={{ fontSize: 13, borderRadius: 8 }} onClick={() => setShowDeleteModal(false)} disabled={deleting}>
                Cancel
              </button>
              <button className="btn btn-danger d-flex align-items-center gap-2" style={{ fontSize: 13, borderRadius: 8, fontWeight: 600 }} onClick={handleDeleteInvoice} disabled={deleting}>
                <Trash2 size={14} /> {deleting ? "Deleting…" : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default InvoiceDetails;
